import { useSubjectMember } from '@healthblocks-io/core'
import { useCallback } from 'react'

import { tenantSlugs } from '@/modules/healthblocks/ServerProvider'
import { useNavigate } from '@/router-wrapper'
import { getUniversalLinkConfig } from '../helpers/universal-link'
import { useLockoutTime } from '../hooks/useLockoutTime'

import type { TenantSlug } from '@/modules/healthblocks/ServerProvider'

function extractTenantFromUrl(parsed: URL): TenantSlug | undefined {
  const pathParts = parsed.pathname.split('/')
  const linkIndex = pathParts.indexOf('link')
  if (linkIndex > 1) {
    const candidate = pathParts[linkIndex - 1] as TenantSlug
    if (tenantSlugs.includes(candidate)) return candidate
  }
  const hostCandidate = parsed.hostname as TenantSlug
  if (tenantSlugs.includes(hostCandidate)) return hostCandidate
  return undefined
}

export function useUniversalLinkHandler() {
  const subjectMember = useSubjectMember()
  const navigate = useNavigate()
  const { lockout } = useLockoutTime()
  const isLoggedIn = !!subjectMember && !lockout

  return useCallback(
    (url: string) => {
      try {
        const parsed = new URL(url)
        const config = getUniversalLinkConfig(url)

        // Require auth?
        if (config.requiresAuth && !isLoggedIn) {
          navigate('/login')
          return
        }

        // Extract path segments after /link/:action
        const pathParts = parsed.pathname.split('/')
        const linkIndex = pathParts.indexOf('link')
        const pathParameters = linkIndex === -1 ? [] : pathParts.slice(linkIndex + 2)

        // Inject params from path segments or query params
        let targetPath = config.path
        const usedParameterKeys = new Set<string>()

        if (config.paramKeys?.length) {
          config.paramKeys.forEach((key, index) => {
            // Try to get value from path segment first, then fall back to query param
            const value = pathParameters[index] || parsed.searchParams.get(key)
            if (value) {
              targetPath = targetPath.replace(`:${key}`, value)
              usedParameterKeys.add(key)
            }
          })
        }

        // Build query string, excluding parameters that were used in the path
        const queryParameters = new URLSearchParams(parsed.search)
        usedParameterKeys.forEach((key) => queryParameters.delete(key))
        const search = queryParameters.toString() ? `?${queryParameters.toString()}` : ''

        // Use the tenant from the deeplink URL so cross-tenant deeplinks navigate correctly
        const tenant = extractTenantFromUrl(parsed)
        if (tenant) {
          navigate(`/${tenant}${targetPath}${search}`, { tenant: false })
        } else {
          navigate(`${targetPath}${search}`)
        }
      } catch (error) {
        console.error('[useUniversalLinkHandler] Failed to handle URL:', url, error)
        navigate('/home')
      }
    },
    [navigate, isLoggedIn],
  )
}
